import { toast } from "react-toastify";
import { Alert, Button, Form, Input, Modal, Popconfirm, Space, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../axios/axios-instance";
import { pathAPI } from "../../../utils/pathApi";

type WebhookClientStatus = "ACTIVE" | "REVOKED";

interface WebhookClient {
  id: number;
  name: string;
  token_prefix: string;
  status: WebhookClientStatus;
  created_date: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  token?: string;
}

interface CreateFormValues {
  name: string;
}

const NAME_MAX_LENGTH = 255;

const unwrap = <T,>(response: { data?: { data?: T } & T }): T =>
  (response?.data?.data ?? response?.data) as T;

const formatDate = (value: string | null) => (value ? value.replace("T", " ").slice(0, 19) : "-");

const WebhookClients = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm<CreateFormValues>();
  const [clients, setClients] = useState<WebhookClient[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [issuedToken, setIssuedToken] = useState<WebhookClient | null>(null);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(pathAPI.webhookClients);
      setClients(unwrap<WebhookClient[]>(response) ?? []);
    } catch (error) {
      toast.error(t("ai_chat.wh.load_error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const createClient = async (values: CreateFormValues) => {
    setCreating(true);
    try {
      const response = await axiosInstance.post(pathAPI.webhookClients, values);
      setIssuedToken(unwrap<WebhookClient>(response));
      setCreateOpen(false);
      form.resetFields();
      fetchClients();
    } catch (error) {
      toast.error(t("ai_chat.wh.create_error"));
    } finally {
      setCreating(false);
    }
  };

  const revokeClient = async (id: number) => {
    try {
      await axiosInstance.put(`${pathAPI.webhookClients}/${id}/revoke`);
      toast.success(t("ai_chat.wh.revoke_ok"));
      fetchClients();
    } catch (error) {
      toast.error(t("ai_chat.wh.revoke_error"));
    }
  };

  const columns: ColumnsType<WebhookClient> = [
    { title: t("ai_chat.wh.col_name"), dataIndex: "name" },
    {
      title: t("ai_chat.wh.col_token"),
      dataIndex: "token_prefix",
      render: (prefix: string) => <code>{prefix}…</code>,
    },
    {
      title: t("ai_chat.wh.col_status"),
      dataIndex: "status",
      render: (status: WebhookClientStatus) => (
        <Tag color={status === "ACTIVE" ? "green" : "default"}>{t(`ai_chat.wh.status.${status}`, { defaultValue: status })}</Tag>
      ),
    },
    { title: t("ai_chat.wh.col_created"), dataIndex: "created_date", render: formatDate },
    { title: t("ai_chat.wh.col_last_used"), dataIndex: "last_used_at", render: formatDate },
    {
      title: "",
      key: "actions",
      render: (_, client) =>
        client.status === "ACTIVE" ? (
          <Popconfirm
            title={t("ai_chat.wh.revoke_title")}
            description={t("ai_chat.wh.revoke_desc")}
            okText={t("ai_chat.wh.revoke")}
            okButtonProps={{ danger: true }}
            onConfirm={() => revokeClient(client.id)}
          >
            <Button danger size="small">
              {t("ai_chat.wh.revoke")}
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: "100%", marginTop: 8 }}>
      <Alert
        type="info"
        showIcon
        message={t("ai_chat.wh.info")}
      />
      <Space>
        <Button type="primary" onClick={() => setCreateOpen(true)}>
          {t("ai_chat.wh.create_btn")}
        </Button>
        <Button onClick={fetchClients} loading={loading}>
          {t("ai_chat.wh.reload")}
        </Button>
      </Space>
      <Table<WebhookClient> rowKey="id" columns={columns} dataSource={clients} loading={loading} pagination={false} />

      <Modal
        title={t("ai_chat.wh.create_title")}
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => form.submit()}
        okText={t("ai_chat.wh.create_ok")}
        confirmLoading={creating}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={createClient}>
          <Form.Item
            name="name"
            label={t("ai_chat.wh.name")}
            extra={t("ai_chat.wh.name_extra")}
            rules={[{ required: true, whitespace: true, message: t("ai_chat.wh.name_required") }]}
          >
            <Input maxLength={NAME_MAX_LENGTH} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("ai_chat.wh.copy_title")}
        open={issuedToken !== null}
        onCancel={() => setIssuedToken(null)}
        footer={<Button type="primary" onClick={() => setIssuedToken(null)}>{t("ai_chat.wh.done")}</Button>}
        maskClosable={false}
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Alert
            type="warning"
            showIcon
            message={t("ai_chat.wh.copy_warning")}
          />
          <Typography.Text type="secondary">{issuedToken?.name}</Typography.Text>
          <Typography.Paragraph copyable code style={{ wordBreak: "break-all", marginBottom: 0 }}>
            {issuedToken?.token}
          </Typography.Paragraph>
        </Space>
      </Modal>
    </Space>
  );
};

export default WebhookClients;
